import type { SdkConfig } from '../core/types';
import type { Identity } from '../core/identity';
import type { Optin } from '../core/optin';
import { scanForm } from '../core/form-scan';
import { log } from '../core/log';

/**
 * Scans `<form>` submissions for an email, feeds the cleartext into
 * `Identity.setEmail()` so it gets hashed client-side and persisted, then records
 * an opt-in tagged with the kind of form it came from — `login`, `register`,
 * `newsletter`, or plain `form` (see `core/form-scan.ts` for the heuristics).
 *
 * Spec §3.3 demands NEVER capturing input values into properties — that still
 * holds. The cleartext only reaches the SHA-256 hasher; the `form_submit` event
 * from `forms.ts` and the `optin` event from here carry metadata only.
 *
 * The opt-in is recorded after the hash resolves, so it always carries the
 * identifier it is announcing.
 */
export function installEmailCapture(config: SdkConfig, identity: Identity, optin: Optin): void {
	if (!config.autoTrack.emailFromForms) return;
	if (typeof document === 'undefined') return;

	document.addEventListener(
		'submit',
		(e: SubmitEvent) => {
			const form = e.target as HTMLFormElement | null;
			if (!form || form.tagName !== 'FORM') return;
			try {
				const scan = scanForm(form);
				if (!scan.email) return;
				identity.setEmail(scan.email).then((hash) => {
					if (!hash) return;
					log('email-capture: hashed from form_submit');
					optin.record(scan.source, scan.context);
				});
			} catch (err) {
				log('email-capture: failed', err);
			}
		},
		true,
	);
}
